<template>
  <!--Base Modal-->
  <base-modal
    :show="open"
    :title="$t('AutoImport')"
    @on-confirm="save()"
    @on-cancel="hide()"
    maxWidth="80%"
    :okText="$t('Save')"
    :closeText="$t('Close')"
  >
    <v-row justify="center" align="center" v-if="loading">
      <div class="text-center">
        <v-progress-circular
          :size="50"
          color="primary"
          indeterminate
        ></v-progress-circular>
      </div>
    </v-row>
    <v-row
      v-if="contentTypeAutoImportConfigs.length == 0 && !loading"
      justify="center"
      align="center"
    >
      <v-col cols="1" align-self="center">
        <v-btn
          tile
          color="primary"
          prepend-icon="mdi-plus"
          outlined
          @click="addContentTypeAutoImportConfig()"
        >
          {{ $t("Add") }}
        </v-btn>
      </v-col>
    </v-row>
    <div v-for="(item, index) in contentTypeAutoImportConfigs" :key="item.uuid">
      <v-row v-if="!loading">
        <v-col cols="1" align-self="center">
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn
                v-on="on"
                tile
                large
                color="error"
                icon
                @click="removeContentTypeAutoImportConfig(item.uuid)"
              >
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("Delete") }}</span>
          </v-tooltip>
        </v-col>
        <v-col class="py-0" align-self="center">
          <v-row justify="center" align="center">
            <v-col align-self="center">
              <v-select
                v-model="item.serviceID"
                :items="services"
                item-value="id"
                item-text="name"
                :placeholder="$t('Service')"
                :error-messages="serviceErrors(index)"
              />
            </v-col>
            <v-col align-self="center">
              <v-text-field
                v-model="item.description"
                :label="$t('Description')"
                :error-messages="descriptionErrors(index)"
              />
            </v-col>
            <!--OneDrive-->
            <v-col cols="1" v-if="item.serviceID == 1" align-self="center">
              <v-btn icon dark color="primary" @click="SignIn(item)">
                <v-icon dark>mdi-account-key-outline</v-icon>
              </v-btn>
            </v-col>
            <v-col v-if="item.serviceID == 1" align-self="center">
              <v-select
                :items="item.folderList"
                v-model="item.rootFolder"
                :disabled="item.folderList.length == 0"
                :label="$t('RootFolder')"
                item-value="id"
                item-text="text"
              />
            </v-col>
            <v-col cols="1" v-if="item.serviceID == 1" align-self="center">
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-btn
                    v-on="on"
                    icon
                    dark
                    color="primary"
                    @click="createFolder(item)"
                    :disabled="item.folderList.length == 0"
                  >
                    <v-icon dark>mdi-folder-plus-outline</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t("CreateFolder") }}</span>
              </v-tooltip>
            </v-col>
            <!--/OneDrive-->
            <!--Email-->
            <v-col cols="1" v-if="item.serviceID == 2" align-self="center">
              <v-btn
                icon
                dark
                color="primary"
                @click="showEmailCredentialsModal(item)"
              >
                <v-icon dark>mdi-account-key-outline</v-icon>
              </v-btn>
            </v-col>
            <v-col v-if="item.serviceID == 2" align-self="center">
              <v-select
                :items="item.folderList"
                v-model="item.rootFolder"
                :disabled="item.folderList.length == 0"
                :label="$t('RootFolder')"
              />
            </v-col>
            <v-col cols="1" v-if="item.serviceID == 2" align-self="center">
              <v-btn icon dark color="primary" @click="getEmailFolders(item)">
                <v-icon dark>mdi-refresh</v-icon>
              </v-btn>
            </v-col>
            <!--/Email-->
            <!--FileSystem-->
            <v-col v-if="item.serviceID == 3" align-self="center">
              <v-text-field
                v-model="item.rootFolder"
                :label="$t('RootFolder')"
              />
            </v-col>
            <v-col cols="1" v-if="item.serviceID == 3" align-self="center">
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-btn
                    v-on="on"
                    icon
                    dark
                    color="primary"
                    @click="checkFolder(item)"
                  >
                    <v-icon dark>mdi-folder-search-outline</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t("Test") }}</span>
              </v-tooltip>
            </v-col>
            <v-col cols="1" v-if="item.serviceID == 3" align-self="center">
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-btn
                    v-on="on"
                    icon
                    dark
                    color="primary"
                    @click="createFolder(item)"
                  >
                    <v-icon dark>mdi-folder-plus-outline</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t("CreateFolder") }}</span>
              </v-tooltip>
            </v-col>
            <!--/FileSystem-->
          </v-row>
          <v-row>
            <v-col class="py-0" align-self="center">
              <v-switch
                v-model="item.readExistingDocuments"
                :label="$t('ReadExistingDocuments')"
              ></v-switch>
            </v-col>
            <v-col class="py-0" align-self="center">
              <v-btn color="primary" outlined @click="editFields(item)">
                {{ $t("Fields") }}
                <v-icon v-if="item.fields.length > 0" right>
                  mdi-pencil
                </v-icon>
                <v-icon v-if="item.fields.length == 0" right> mdi-plus </v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="1" align-self="center">
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn
                v-on="on"
                tile
                large
                color="primary"
                icon
                @click="addContentTypeAutoImportConfig()"
              >
                <v-icon>mdi-plus</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("Add") }}</span>
          </v-tooltip>
        </v-col>
      </v-row>
    </div>
    <ContentTypeAutoImportConfigFieldsModal
      :ref="fieldsModalKey"
      :contentTypeId="contentType.id"
      :contentTypeFields="contentType.fields"
      @on-saved="onFieldsSaved"
    />
    <ContentTypeAutoImportConfigModalCreateFolder
      :ref="folderModalKey"
      @crateFolder="getAutoImportConfigs"
      @on-saved="getFoldersFromConfigs"
    />
    <ContentTypeAutoImportConfigModalEmailLogin
      :ref="emailModalKey"
      @emailConfigurationSaved="saveEmailConfig"
    />
  </base-modal>
</template>

<script>
import { validationMixin } from "vuelidate";
import { required } from "vuelidate/lib/validators";
import AutoImportService from "@/services/AutoImportService";
import Utils from "@/ecm/common/Utils";
import ContentType from "@/ecm/models/contentType/ContentType";
import ContentTypeService from "@/services/ContentTypeService";
import DocumentService from "@/services/DocumentService";
import ContentTypeAutoImportConfig from "@/ecm/models/contentType/autoImport/ContentTypeAutoImportConfig";
import ContentTypeAutoImportConfigFieldsModal from "@/components/administration/contentType/autoImport/ContentTypeAutoImportConfigFieldsModal";
import ContentTypeAutoImportConfigModalCreateFolder from "@/components/administration/contentType/autoImport/ContentTypeAutoImportConfigModalCreateFolder";
import ContentTypeAutoImportConfigModalEmailLogin from "@/components/administration/contentType/autoImport/ContentTypeAutoImportConfigEmailLogin";
import { DxValueFormat } from "devextreme-vue/data-grid";
export default {
  components: {
    ContentTypeAutoImportConfigFieldsModal,
    ContentTypeAutoImportConfigModalCreateFolder,
    ContentTypeAutoImportConfigModalEmailLogin,
  },
  data() {
    return {
      modal: {
        key: "autoImportConfigModalKey",
      },
      parametersLoginEmail: [],
      fieldsModalKey: "fieldModalKey",
      folderModalKey: "folderModalKey",
      emailModalKey: "emailModalKey",
      currentItem: new ContentTypeAutoImportConfig(),
      hasAutomaticRecognition: false,
      contentTypeAutoImportConfigs: [],
      contentTypeRequiredFields: [],
      contentTypeUniqueFields: [],
      open: false,
      loading: false,
      contentType: new ContentType(),
      services: [
        {
          id: 1,
          name: "OneDrive",
        },
        {
          id: 2,
          name: "Email",
        },
        {
          id: 3,
          name: "File system",
        },
      ],
    };
  },
  mixins: [validationMixin],
  validations: {
    contentTypeAutoImportConfigs: {
      $each: {
        description: { required },
        serviceID: { required },
      },
    },
  },
  computed: {},

  methods: {
    descriptionErrors(index) {
      const errors = [];
      if (!this.$v.contentTypeAutoImportConfigs.$each[index].description.$dirty)
        return errors;
      !this.$v.contentTypeAutoImportConfigs.$each[index].description.required &&
        errors.push(this.$t("RequiredField"));
      return errors;
    },
    serviceErrors(index) {
      const errors = [];
      if (!this.$v.contentTypeAutoImportConfigs.$each[index].serviceID.$dirty)
        return errors;
      !this.$v.contentTypeAutoImportConfigs.$each[index].serviceID.required &&
        errors.push(this.$t("RequiredField"));
      return errors;
    },
    show() {
      this.open = true;
    },
    hide() {
      this.open = false;
    },
    edit(dto) {
      this.contentType = new ContentType(dto);
      DocumentService.getStructure(this.contentType.id).then((data) => {
        this.hasAutomaticRecognition = data.HasAutomaticRecognition;
      });
      this.getAutoImportConfigs();
      this.loading = false;
      ContentTypeService.getFields([this.contentType.id]).then((data) => {
        this.contentTypeRequiredFields = data.filter(
          (field) => field.IsRequired
        );
        this.contentTypeUniqueFields = data.filter((field) => field.IsUnique);
        if (
          data.filter((field) => field.IsUnique && field.IsRequired).length > 0
        ) {
          Utils.toast.warning(this.$t("UniqueAndRequiredInContentType"));
        }
      });
      this.show();
    },
    getAutoImportConfigs() {
      this.loading = true;
      ContentTypeService.getAutoImportConfigs(this.contentType.id).then(
        (data) => {
          this.contentTypeAutoImportConfigs = data.map(
            (dto) => new ContentTypeAutoImportConfig(dto)
          );
          this.getFoldersFromConfigs();
          this.loading = false;
        }
      );
    },
    getFoldersFromConfigs() {
      this.contentTypeAutoImportConfigs.forEach((x) => {
        if (x.serviceID == 1 || x.oAuthRefreshToken != "") {
          x.folderList = [];
          var token = {
            RefreshToken: x.oAuthRefreshToken,
            AccessToken: null,
          };
          AutoImportService.getOneDriveFolders(token).then((data) => {
            x.folderList = data;
          });
        } else if (x.serviceID == 2) {
          this.getEmailFolders(x);
        }
      });
    },
    async SignIn(item) {
      var userID = this.$store.getters["account/user"].Id;
      var apiUrl = this.$store.getters["account/apiUrl"];
      var parameters = await AutoImportService.getOneDriveEndpointParameters();
      var windowOBJ = window.open(
        `${parameters.ApiURL}/authorize?client_id=${parameters.ClientID}&scope=offline_access%20user.read%20mail.read%20files.readwrite&response_type=code&state=${userID}&redirect_uri=${apiUrl}/api/auto-import/onedrive/set-token`,

        "resizable=yes,status=yes,location=yes"
      );

      var token = await AutoImportService.getOneDriveToken(userID);
      debugger;
      windowOBJ.close();
      if (token) {
        item.oAuthAccessToken = token.AccessToken;
        item.oAuthRefreshToken = token.RefreshToken;
        item.folderList = await AutoImportService.getOneDriveFolders(token);
      }
    },
    saveEmailConfig(loginEmailParametersModal) {
      this.currentItem.emailAccount = loginEmailParametersModal.emailAccount;
      this.currentItem.emailPassword = loginEmailParametersModal.emailPassword;
      this.currentItem.emailServer = loginEmailParametersModal.emailServer;
      this.currentItem.emailPort = loginEmailParametersModal.emailPort;
      this.currentItem.emailUseSSL = loginEmailParametersModal.emailUseSSL;

      this.getEmailFolders(this.currentItem);
    },

    getEmailFolders(item) {
      AutoImportService.getEmailFolders(item.toDto()).then((data) => {
        item.folderList = data;
        if (item.folderList.some((x) => x == "INBOX")) {
          item.rootFolder = "INBOX";
        } else {
          item.rootFolder = "Inbox";
        }
      });
    },
    save() {
      this.loading = true;
      this.$v.$touch();
      if (this.$v.$invalid) {
        this.loading = false;
        return;
      }
      var incompleteConfigs = this.contentTypeAutoImportConfigs
        .filter(
          (item) =>
            (item.serviceID == 2 && item.emailAccount == "") ||
            (item.serviceID == 1 && item.oAuthRefreshToken == "")
        )
        .map((item) => item.description);
      if (incompleteConfigs.length != 0) {
        var message = incompleteConfigs.join(",");
        this.loading = false;
        Utils.toast.error(this.$t("NullParametersEmail") + message);
        return;
      }

      if (this.hasAutomaticRecognition == false) {
        let requiredFieldsCodes = this.contentTypeRequiredFields.map(
          (requiredField) => requiredField.Code
        );
        let configurationsWithEmptyRequiredFields =
          this.contentTypeAutoImportConfigs
            .filter((config) =>
              config.fields.some(
                (field) =>
                  !field.value &&
                  requiredFieldsCodes.includes(field.defFieldCode)
              )
            )
            .map((item) => item.description);
        if (configurationsWithEmptyRequiredFields.length > 0) {
          var errorMessage = configurationsWithEmptyRequiredFields.join(",");
          Utils.toast.error(
            this.$t("PendingFieldsConfiguration") +
              errorMessage +
              this.$t("NoAutomaticRecognition")
          );
          this.loading = false;
          return;
        }
      }
      let uniqueFieldsCodes = this.contentTypeUniqueFields.map(
        (uniqueField) => uniqueField.Code
      );
      let configurationsWithNotEmptyUniqueFields =
        this.contentTypeAutoImportConfigs
          .filter((config) =>
            config.fields.some(
              (field) =>
                field.value != "" &&
                uniqueFieldsCodes.includes(field.defFieldCode)
            )
          )
          .map((item) => item.description);
      if (configurationsWithNotEmptyUniqueFields.length > 0) {
        var errorMessage = configurationsWithNotEmptyUniqueFields.join(",");
        Utils.toast.error(this.$t("UniqueFieldsWithValue") + errorMessage);
        this.loading = false;
        return;
      }
      var autoImportConfigs = this.contentTypeAutoImportConfigs.map((item) =>
        item.toDto()
      );
      ContentTypeService.saveAutoImportConfigs(
        this.contentType.id,
        autoImportConfigs
      ).then((data) => {
        this.loading = false;
        this.hide();
      });
    },
    addContentTypeAutoImportConfig() {
      this.contentTypeAutoImportConfigs.push(new ContentTypeAutoImportConfig());
    },
    removeContentTypeAutoImportConfig(uuid) {
      this.contentTypeAutoImportConfigs =
        this.contentTypeAutoImportConfigs.filter((item) => item.uuid !== uuid);
    },
    editFields(item) {
      this.currentItem = item;
      this.$refs[this.fieldsModalKey].edit(item.fields);
    },
    onFieldsSaved(fields) {
      this.currentItem.fields = fields;
    },
    createFolder(item) {
      this.$refs[this.folderModalKey].show(item.oAuthRefreshToken);
    },
    showEmailCredentialsModal(item) {
      this.currentItem = item;
      this.$refs[this.emailModalKey].show(item);
    },
    checkFolder(item) {
      var folder = {
        FolderPath: item.rootFolder,
      };
      AutoImportService.checkFolder(folder).then((result) => {
        if (result) {
          Utils.toast.success(this.$t("CorrectFolderPath"));
        } else {
          Utils.toast.error();
          this.$t("IncorrectFolderPath");
        }
      });
    },
  },
};
</script>
<style>
</style>